/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 16-09-2026
 *
 * De brug tussen een node op het simcanvas en de simulatiedata waar hij bij hoort.
 *
 * Tot golf 3.6 droeg elke node zijn eigen kopie: een state-node had de hele state in `data.sim`, en
 * een transitienode zowel de transitie (`data.sim`) als de volledige bronstate (`data.statesim`).
 * Daarmee stond de simulatie drie keer in de simulatiestatus — gemeten op de grootste snapshot in de
 * devdatabase: 433 van de 444 KB was duplicatie. Die status gaat naar elke volger bij elke
 * standwijziging en in elke snapshot naar de database, en aan de ontvangende kant maakt `JSON.parse`
 * van elke kopie een eigen objectgraaf.
 *
 * De sleutels om het terug te vinden stonden er al naast en worden nog steeds meegeschreven: `num` op
 * een state-node, `statenum` + `transitietype` + `identifier` op een transitienode, en de index in het
 * node-id (`sn3o1`). Daarom is er geen formaatversie nodig: deze lezers kijken nooit meer naar
 * `data.sim`, of dat veld er nu in staat of niet, en werken dus ongewijzigd op elke bestaande snapshot.
 */

import type { NodeSingular } from 'cytoscape';
import type { SimulatieClosure, SimulatieData, SimulatieState, SimulatieTransitie } from './dl-simcanvas.component';

/**
 * De state waar deze node bij hoort. Voor een state-node is dat de state zelf, voor een transitienode
 * de state waar de transitie uit vertrekt.
 * @param simulatie de simulatie waar deze node in getekend is
 * @param node een state- of transitienode
 */
export function stateVanNode(simulatie: SimulatieData | null | undefined,
                             node: NodeSingular | null | undefined): SimulatieState | null {
    const num = node?.data('type') === 'transitie' ? node.data('statenum') : node?.data('num');
    return (num === undefined || num === null) ? null : (simulatie?.states?.['s' + num] ?? null);
}

/**
 * De transitie waar deze transitienode bij hoort, gevonden op zijn type en de index in zijn id.
 *
 * Let op de closure-tak: het tekenen gebruikt daar alleen de closures zónder opvolger
 * (`closures.filter(c => !c.to.length)`), dus de index slaat op die gefilterde lijst en de lookup moet
 * dezelfde filter toepassen. Als vangnet — en voor de zekerheid bij oude snapshots — wordt er nog op
 * `identifier` gecontroleerd zodra die er is.
 * @param simulatie de simulatie waar deze node in getekend is
 * @param node een transitienode
 */
export function transitieVanNode(simulatie: SimulatieData | null | undefined,
                                 node: NodeSingular | null | undefined): SimulatieTransitie | null {
    const state = stateVanNode(simulatie, node);
    if (!state || !node) {
        return null;
    }
    const lijst = transitielijst(state, node.data('transitietype') as string);
    const index = indexUitId(node.id());
    const gevonden = (index === null) ? null : (lijst[index] ?? null);
    const identifier = node.data('identifier');
    if (gevonden && (!identifier || gevonden.identifier === identifier)) {
        return gevonden;
    }
    //de index wees niet (meer) de goede aan: dan is de identifier de tweede ingang
    return identifier ? (lijst.find(transitie => transitie.identifier === identifier) ?? null) : gevonden;
}

/**
 * De transities van een state die bij dit transitietype horen, in de volgorde waarin ze getekend zijn.
 */
function transitielijst(state: SimulatieState, transitietype: string): SimulatieTransitie[] {
    switch (transitietype) {
        case 'terminatie':
            return state.terminaties;
        case 'ordering':
            return state.orderings;
        case 'closure':
            return (state.closures as SimulatieClosure[]).filter(c => !c.to.length);
        default:
            return [];
    }
}

/**
 * De index die achter een transitie-id staat: `sn3o1` is de tweede ordering van state 3.
 */
function indexUitId(id: string): number | null {
    const cijfers = /(\d+)$/.exec(id);
    return cijfers ? Number(cijfers[1]) : null;
}
